import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import TextField from '@mui/material/TextField';
import { api } from '@/api';
import { color } from '@/styles';
import { PanelContainer } from '@/components/panel';
import { numBetweenMinMax } from '@/utils/dataFormatUtil';
import {
  DEFECATION_TYPE_JA,
  DEFECATION_TYPE_EN,
  PAD_TYPE_JA,
  PAD_TYPE_EN,
  TOILET_TYPE_JA,
  TOILET_TYPE_EN,
  URINE_TYPE_JA,
  URINE_TYPE_EN,
} from '@/constants';
import { navSelector, navSlice } from '@/redux/config/leftnav';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { DateSelector } from '@/components/mui/dateSelector';
import { MenuItemProps, Select } from '@/components/mui/select';
import { CareRecordButtonGroup } from '@/components/mui/radio/Radio';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useFetchResident } from '@/hooks/api/resident/useFetchResident';
import { useFetchCareRecord } from '@/hooks/api/careRecord/useFetchCareRecord';
import { useFetchNursingHomeDiapers } from '@/hooks/api/careSettings/useFetchNursingHomeDiapers';
import { useFetchLaxatives } from '@/hooks/api/careSettings/useFetchLaxatives';
import { useFetchDrinks } from '@/hooks/api/careSettings/useFetchDrinks';
import { ICreateCareRecordParams } from '@/types/api/care-record/createCareRecord';
import { ICareRecordDiaper } from '@/types/api/care-record/careRecordDiaper';
import { IUpdateCareRecordParams } from '@/types/api/care-record/updateCareRecord';
import { IUpdateUrineVolumeReset } from '@/types/api/resident/updateUrineVolumeReset';
import { Button } from '@/components/mui/button';
import { useLoading } from '@/contexts/LoadingContext';
import StoolKorokoro from '@/components/svg/stoolKorokoro';
import StoolHard from '@/components/svg/stoolHard';
import StoolLittleHard from '@/components/svg/stoolLittleHard';
import StoolMiddle from '@/components/svg/stoolMiddle';
import StoolSoft from '@/components/svg/stoolSoft';
import StoolLittleSoft from '@/components/svg/stoolLittleSoft';
import StoolWater from '@/components/svg/stoolWater';

const Title = css`
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
`;

const AlignItems = css`
  display: flex;
  align-items: center;
`;

const AlignStart = css`
  display: flex;
  align-items: flex-start;
`;

const DirectionColumn = css`
  display: flex;
  flex-direction: column;
`;

const Wrap = css`
  flex-wrap: wrap;
`;

const ContentGap = css`
  display: flex;
  gap: 16px;
`;

const Wmax = css`
  width: 100%;
`;

const W100 = css`
  flex-shrink: 0;
  width: 100px;
  margin: 4px 0 12px 0;
`;

const W200 = css`
  flex-shrink: 0;
  width: 200px;
  margin: 4px 0 12px 0;
`;

const W300 = css`
  flex-shrink: 0;
  width: 300px;
  margin: 4px 0 12px 0;
`;

const W350 = css`
  flex-shrink: 0;
  width: 350px;
  margin: 4px 0 12px 0;
`;

const W400 = css`
  flex-shrink: 0;
  width: 400px;
  margin: 4px 0 12px 0;
`;

const MarginTop = css`
  margin-top: 14px;
`;

const MarginBottom = (size: string) => css`
  margin-bottom: ${size};
`;

const ContentMargin = css`
  margin: 0 0 12px 0;
`;

const BottomLine = css`
  border-bottom: 1px solid ${color.green};
`;

const ContentLeft = css`
  width: 136px;
  padding: 16px 16px;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
  color: ${color.white};
  background-color: ${color.green};
  border-bottom: 1px solid ${color.white};
`;

const ContentWrapper = css`
  display: flex;
  color: ${color.dark_gray};
`;

const ContentRight = css`
  width: 100%;
  padding: 20px 32px 16px 24px;
  background-color: ${color.light_green};
  border: 1px solid ${color.green};
  border-bottom: none;
  gap: 16px;
`;

const ButtonWrapper = css`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

const TableLabel = css`
  margin: 8px;
  font-size: 14px;
`;

const FormText = css`
  margin: 0 0 4px 4px;
  font-size: 16px;
`;

const Image = css`
  margin: 0;
  width: 24px;
  min-width: 24px;
  height: 24px;
`;

const ErrorText = css`
  color: ${color.red};
  font-size: 14px;
`;

const Unit = css`
  font-size: 14px;
  margin: 16px 8px 0 8px;
`;

export const DetailedRecordPlus = React.memo(() => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { residentId } = useParams<{ residentId: string }>();
  const { careRecordId } = useParams<{ careRecordId: string }>();
  const { resident } = useFetchResident(true, +residentId);
  const { badge } = useSelector(navSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { careRecord, fetchCareRecord } = useFetchCareRecord(true, +careRecordId);
  const { nursingHomeDiapers, nursingHomePants, nursingHomePads } = useFetchNursingHomeDiapers(true, 'asc');
  const { laxatives } = useFetchLaxatives(true, 'asc');
  const { drinks } = useFetchDrinks(true, 'asc');
  const initialDate = dayjs();
  const { t, i18n } = useTranslation();
  const [onSend, setOnSend] = React.useState<boolean>(false);

  // formData
  const [date, setDate] = React.useState<Dayjs>(initialDate);
  const [hour, setHour] = React.useState<number>();
  const [minute, setMinute] = React.useState<number>();
  const [staffName, setStaffName] = React.useState<string | null>(null);
  const [workingTime, setWorkingTime] = React.useState<number | null>(5);
  const [ruv, setRuv] = React.useState<number | null>(null);
  const [toiletType, setToiletType] = React.useState<MenuItemProps | null>(null);
  const [isUrinatedToilet, setIsUrinatedToilet] = React.useState<boolean | null>(null);
  const [toiletVolume, setToiletVolume] = React.useState<number | ''>('');
  const [padType, setPadType] = React.useState<MenuItemProps | null>(null);
  const [padUrineVolumeType, setPadUrineVolumeType] = React.useState<string | null>(null);
  const [padUrineVolume, setPadUrineVolume] = React.useState<number | ''>('');
  const [isUrineLeakage, setIsUrineLeakage] = React.useState<boolean | null>(null);
  const [isUrgency, setIsUrgency] = React.useState<boolean | null>(null);
  const [isLeakOutUrine, setIsLeakOutUrine] = React.useState<boolean | null>(null);
  const [defecationVolumeType, setDefecationVolumeType] = React.useState<string | null>(null);
  const [hardnessType, setHardnessType] = React.useState<string | null>(null);
  const [isLeakOutFeces, setIsLeakOutFeces] = React.useState<boolean | null>(null);
  const [pantId, setPantId] = React.useState<MenuItemProps | null>(null);
  const [pad1Id, setPad1Id] = React.useState<MenuItemProps | null>(null);
  const [pad2Id, setPad2Id] = React.useState<MenuItemProps | null>(null);
  const [laxativeId, setLaxativeId] = React.useState<MenuItemProps | null>(null);
  const [laxativeAmount, setLaxativeAmount] = React.useState<number | ''>('');
  const [drinkId, setDrinkId] = React.useState<MenuItemProps | null>(null);
  const [otherDrink, setOtherDrink] = React.useState<string | ''>('');
  const [drinkVolumeType, setDrinkVolumeType] = React.useState<number | ''>('');
  const [drinkVolume, setDrinkVolume] = React.useState<number | ''>('');
  const [memo, setMemo] = React.useState<string | ''>('');

  // formErrorControl
  const [isDateError, setDateError] = React.useState<boolean>(false);
  const [urinatedToiletErrorText, setUrinatedToiletErrorText] = React.useState<string>('');
  const [padUrineVolumeTypeErrorText, setPadUrineVolumeTypeErrorText] = React.useState<string>('');
  const [drinkVolumeTypeErrorText, setDrinkVolumeTypeErrorText] = React.useState<string>('');
  const [isOtherDrinkError, setOtherDrinkError] = React.useState<boolean>(false);
  const [otherDrinkErrorText, setOtherDrinkErrorText] = React.useState<string>('');
  const [laxativeAmountErrorText, setLaxativeAmountErrorText] = React.useState<string>('');
  const [hardnessTypeFormErrorText, setHardnessTypeFormErrorText] = React.useState<string>('');
  const [defecationVolumeFormWarningText, setDefecationVolumeFormWarningText] = React.useState<string>('');
  const [laxativeAmountFormWarningText, setLaxativeAmountFormWarningText] = React.useState<string>('');

  const unSelectedOption = { key: '-', value: t('notSelected', '未選択') };

  // api
  const createCareRecord = async (data: ICreateCareRecordParams) => {
    return await api.post('/residents/:residentId/care-records', data);
  };

  const updateCareRecord = async (data: IUpdateCareRecordParams | any) => {
    return await api.put('/care-records/:careRecordId', data);
  };

  const updateUrineVolumeReset = async (residentId: number) => {
    const data: IUpdateUrineVolumeReset = {
      residentId,
      urineVolumeResetFlg: true,
    };
    return await api.put('/residents/:residentId/urine-volume-reset', data);
  };

  // TODO: Editの場合フォームの初期値がない場合にバグ
  // TODO: コンポーネント生成時に1回だけ実行させる(以下の他のリストも同様）
  // ItemList
  const hourList = React.useMemo(() => {
    const hourPerDay = 24; //24時間
    let hourList: MenuItemProps[] = [];
    for (let i = 0; i < hourPerDay; i++) {
      hourList.push({
        key: i,
        value: String(i),
      });
    }
    return hourList;
  }, []);

  const minuteList = React.useMemo(() => {
    const minPerHour = 60; //60分
    let minuteList: MenuItemProps[] = [];
    for (let i = 0; i < minPerHour / 1; i++) {
      minuteList.push({
        key: 1 * i,
        value: String(1 * i),
      });
    }
    return minuteList;
  }, [careRecord]);

  const toiletTypeList = React.useMemo(() => {
    const array = Object.entries(i18n.language.startsWith('ja') ? TOILET_TYPE_JA : TOILET_TYPE_EN);
    return [
      unSelectedOption,
      ...array.map((v) => {
        return { key: v[0], value: v[1] };
      }),
    ];
  }, []);

  const booleanButtonList = [
    { key: true, value: t('yes', '有') },
    { key: false, value: t('no', '無') },
  ];

  const padTypeList = React.useMemo(() => {
    const array = Object.entries(i18n.language.startsWith('ja') ? PAD_TYPE_JA : PAD_TYPE_EN);
    return [
      unSelectedOption,
      ...array.map((v) => {
        return { key: v[0], value: v[1] };
      }),
    ];
  }, []);

  const padUrineVolumeTypeList = React.useMemo(() => {
    const array = Object.entries(i18n.language.startsWith('ja') ? URINE_TYPE_JA : URINE_TYPE_EN);
    return array.map((v) => {
      return { key: v[0], value: v[1] };
    });
  }, []);

  const defecationVolumeTypeList = React.useMemo(() => {
    const array = Object.entries(i18n.language.startsWith('ja') ? DEFECATION_TYPE_JA : DEFECATION_TYPE_EN);
    return array.map((v) => {
      return { key: v[0], value: v[1] };
    });
  }, []);

  const hardnessTypeList = [
    {
      key: 'korokoro',
      value: t('コロコロ', 'コロコロ'),
      iconComponent: StoolKorokoro,
      isNarrowText: true,
    },
    { key: 'hard', value: t('hard', '硬'), iconComponent: StoolHard },
    { key: 'littleHard', value: t('やや硬', 'やや硬'), iconComponent: StoolLittleHard },
    { key: 'middle', value: t('normal', '普通'), iconComponent: StoolMiddle },
    { key: 'soft', value: t('soft', 'やや軟'), iconComponent: StoolSoft },
    { key: 'littleSoft', value: t('mushy', '泥状'), iconComponent: StoolLittleSoft },
    {
      key: 'water',
      value: t('waterly', '水様状'),
      iconComponent: StoolWater,
    },
  ];

  const pantsList = React.useMemo(() => {
    if (!nursingHomePants) return [];
    return [
      unSelectedOption,
      ...nursingHomePants.map((v) => {
        return { key: v.id, value: v.diaperName };
      }),
    ];
  }, [nursingHomePants]);

  const padsList = React.useMemo(() => {
    if (!nursingHomePads) return [];
    return [
      unSelectedOption,
      ...nursingHomePads.map((v) => {
        return { key: v.id, value: v.diaperName };
      }),
    ];
  }, [nursingHomePads]);

  const laxativeList = React.useMemo(() => {
    if (!laxatives) return [];
    return [
      unSelectedOption,
      ...laxatives.map((v) => {
        return { key: v.id, value: v.name };
      }),
    ];
  }, [laxatives]);

  const drinkList = React.useMemo(() => {
    if (!drinks) return [];
    const list = [
      unSelectedOption,
      ...drinks.map((v) => {
        return { key: v.id, value: v.name };
      }),
      { key: 0, value: t('other', 'その他') },
    ];
    return list;
  }, [drinks]);

  const drinkVolumeList = [
    { key: 150, value: '150ml' },
    { key: 200, value: '200ml' },
    { key: 0, value: t('other', 'その他') },
  ];

  // initialValue
  const initialHour = React.useMemo(() => {
    if (!hourList) return undefined;
    if (!careRecord || !careRecord.timestamp) {
      setHour(+hourList[initialDate.hour()].key);
      return hourList[initialDate.hour()];
    } else {
      setHour(dayjs(careRecord.timestamp).hour());
      return hourList[dayjs(careRecord.timestamp).hour()];
    }
  }, [hourList, careRecord]);

  const initialMinute = React.useMemo(() => {
    const interval = 1; //1分
    if (!minuteList) return undefined;
    if (!careRecord || !careRecord.timestamp) {
      const index = Math.floor(initialDate.minute() / interval);
      setMinute(+minuteList[index].key);
      return minuteList[index];
    } else {
      const index = minuteList.findIndex((v) => {
        return v.key === dayjs(careRecord.timestamp).minute();
      });
      if (index) {
        setMinute(dayjs(careRecord.timestamp).minute());
        return minuteList[index];
      } else {
        setMinute(dayjs(careRecord.timestamp).minute());
        return minuteList[0];
      }
    }
  }, [minuteList, careRecord]);

  // notSelectedCheck
  const isEmptyToiletType = React.useMemo(() => {
    return JSON.stringify(toiletType) === JSON.stringify(toiletTypeList[0]);
  }, [toiletType]);

  const isEmptyPadType = React.useMemo(() => {
    return JSON.stringify(padType) === JSON.stringify(padTypeList[0]);
  }, [padType, padTypeList]);

  const isEmptyPantId = React.useMemo(() => {
    return JSON.stringify(pantId) === JSON.stringify(pantsList[0]);
  }, [pantId, pantsList]);

  const isEmptyPad1Id = React.useMemo(() => {
    return JSON.stringify(pad1Id) === JSON.stringify(padsList[0]);
  }, [pad1Id, padsList]);

  const isEmptyPad2Id = React.useMemo(() => {
    return JSON.stringify(pad2Id) === JSON.stringify(padsList[0]);
  }, [pad2Id, padsList]);

  const isEmptyLaxativeId = React.useMemo(() => {
    return JSON.stringify(laxativeId) === JSON.stringify(laxativeList[0]);
  }, [laxativeId, laxativeList]);

  const isEmptyDrinkId = React.useMemo(() => {
    return JSON.stringify(drinkId) === JSON.stringify(drinkList[0]);
  }, [drinkId, drinkList]);

  // formControl
  const isDisabledIsUrinatedToiletForm = React.useMemo(() => {
    return !toiletType || isEmptyToiletType;
  }, [careRecord, toiletType, isEmptyToiletType]);

  const isDisableToiletVolumeForm = React.useMemo(() => {
    // トイレ排尿（有）の場合、操作できる
    if (isUrinatedToilet) {
      return false;
    }
    // 操作できないとき、入力内容をクリアする
    setToiletVolume('');
    return true;
  }, [careRecord, toiletType, isUrinatedToilet]);

  const isDisabledPadUrineVolumeTypeForm = React.useMemo(() => {
    return !padType || isEmptyPadType;
  }, [careRecord, padType, isEmptyPadType]);

  const isDisabledPadUrineVolumeForm = React.useMemo(() => {
    // 尿量が多・中・少の場合、操作できる
    if (!!padUrineVolumeType && padUrineVolumeType !== 'none') {
      return false;
    }
    // 操作できないとき、入力内容をクリアする
    setPadUrineVolume('');
    return true;
  }, [careRecord, padUrineVolumeType]);

  const isDisabledUrgency = React.useMemo(() => {
    // トイレ排尿（有）の場合、おむつの尿量（多・中・少）の場合、操作できる
    if (isUrinatedToilet || (!!padUrineVolumeType && padUrineVolumeType !== 'none')) {
      return false;
    }
    // 操作できないとき、入力内容をクリアする
    setIsUrgency(null);
    setIsLeakOutUrine(null);
    return true;
  }, [careRecord, isUrinatedToilet, padUrineVolumeType]);

  // 排便分析の算出のため、排便と下剤の同時入力を許容しないよう制限
  const isDisabledDefecationVolumeForm = React.useMemo(() => {
    return !!laxativeId?.key && !isEmptyLaxativeId;
  }, [careRecord, laxativeId, isEmptyLaxativeId]);

  const isDisabledHardnessTypeForm = React.useMemo(() => {
    return !defecationVolumeType || defecationVolumeType === 'none';
  }, [careRecord, defecationVolumeType]);

  // 排便量の入力があるとき、無以外は便汚染の操作できる
  const isDisabledLeakOutFeces = React.useMemo(() => {
    return !defecationVolumeType || defecationVolumeType === 'bit' || defecationVolumeType === 'none';
  }, [careRecord, defecationVolumeType]);

  const isDisabledPad2IdForm = React.useMemo(() => {
    return !pad1Id;
  }, [careRecord, pad1Id]);

  // 排便分析の算出のため、排便と下剤の同時入力を許容しないよう制限
  const isDisabledLaxativeIdForm = React.useMemo(() => {
    return !!defecationVolumeType || isLeakOutFeces !== null;
  }, [careRecord, defecationVolumeType, isLeakOutFeces]);

  const isDisabledLaxativeAmountForm = React.useMemo(() => {
    return !(laxativeId && !isEmptyLaxativeId);
  }, [careRecord, laxativeId, isEmptyLaxativeId]);

  const isDisabledOtherDrinkForm = React.useMemo(() => {
    return drinkId?.key !== 0 && !careRecord?.drinkingOther;
  }, [careRecord, drinkId, isEmptyDrinkId]);

  const isDisabledDrinkVolumeTypeForm = React.useMemo(() => {
    const isDrinkInitLoading = !!careRecord && careRecord.drinkingVolume != null && drinkId === null;
    return !isDrinkInitLoading && (!drinkId || isEmptyDrinkId);
  }, [careRecord, drinkId, isEmptyDrinkId]);

  const isDisabledDrinkVolumeForm = React.useMemo(() => {
    if (drinkVolumeType === 0) {
      return false;
    }

    setDrinkVolume('');
    return true;
  }, [careRecord, drinkVolumeType]);

  // form-change-event and validationCheck
  const handleChangeDate = (date: Dayjs) => {
    setDate(date);
    setDateError(false);
  };

  const handleChangeHour = (hour: MenuItemProps) => {
    setHour(+hour.key);
    setDateError(false);
  };

  const handleChangeMinute = (min: MenuItemProps) => {
    setMinute(+min.key);
    setDateError(false);
  };

  const handleChangeStaffName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStaffName(e.target.value);
  };

  const handleChangeWorkingTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingTime(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeToiletType = (data: MenuItemProps) => {
    setToiletType(data);
  };

  const handleChangeIsUrinatedToilet = (data: string | number | boolean | null) => {
    setUrinatedToiletErrorText('');
    setIsUrinatedToilet(data === 'true' ? true : data === 'false' ? false : null);
  };

  const handleChangeToiletVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToiletVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangePadType = (data: MenuItemProps) => {
    setPadType(data);
  };

  const handleChangePadUrineVolumeType = (data: string | number | boolean | null) => {
    setPadUrineVolumeTypeErrorText('');
    setPadUrineVolumeType(data ? String(data) : null);
  };

  const handleChangePadUrineVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPadUrineVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeIsUrineLeakage = (data: string | number | boolean | null) => {
    setIsUrineLeakage(data === 'true' ? true : data === 'false' ? false : null);
  };

  const handleChangeIsUrgency = (data: string | number | boolean | null) => {
    setIsUrgency(data === 'true' ? true : data === 'false' ? false : null);
  };

  const handleChangeIsUrinePollution = (data: string | number | boolean | null) => {
    setIsLeakOutUrine(data === 'true' ? true : data === 'false' ? false : null);
  };

  const handleChangeDefecationVolumeType = (data: string | number | boolean | null) => {
    setHardnessTypeFormErrorText('');
    setDefecationVolumeType(data ? String(data) : null);
  };

  const handleChangeHardnessType = (data: string | number | boolean | null) => {
    setHardnessTypeFormErrorText('');
    setHardnessType(data ? String(data) : null);
  };

  const handleChangeIsStoolPollution = (data: string | number | boolean | null) => {
    setIsLeakOutFeces(data === 'true' ? true : data === 'false' ? false : null);
  };

  const handleChangePantId = (data: MenuItemProps) => {
    setPantId(data);
  };

  const handleChangePad1Id = (data: MenuItemProps) => {
    setPad1Id(data);
  };

  const handleChangePad2Id = (data: MenuItemProps) => {
    setPad2Id(data);
  };

  const handleChangeLaxativeId = (data: MenuItemProps) => {
    setLaxativeAmountErrorText('');
    setLaxativeId(data);
    if (data) {
      setIsLeakOutFeces(null);
    }
  };

  const handleChangeLaxativeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLaxativeAmountErrorText('');
    setLaxativeAmount(numBetweenMinMax(+e.target.value, 1, 9999));
  };

  const handleChangeDrinkId = (data: MenuItemProps) => {
    setDrinkId(data);
    if (data.key !== 0) {
      setOtherDrink('');
      setOtherDrinkErrorText('');
      setOtherDrinkError(false);
    }
  };

  const handleChangeOtherDrink = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherDrinkError(false);
    setOtherDrinkErrorText('');
    setOtherDrink(e.target.value);
  };

  const handleChangeDrinkVolumeType = (data: string | number | boolean | null) => {
    setDrinkVolumeTypeErrorText('');
    setDrinkVolumeType(data ? +data : '');
    if (data === '0') setDrinkVolume(100);
    else if (data !== '0') setDrinkVolume('');
  };

  const handleChangeDrinkVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDrinkVolume(numBetweenMinMax(+e.target.value, 0, 9999));
  };

  const handleChangeMemo = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemo(e.target.value);
  };

  const handleClickSubmit = async () => {
    // バリデーションにエラーがあるかどうか（trueでエラー有り）
    let hasValidationErrors = false;

    // 各formからデータを収集
    let sendData: ICreateCareRecordParams = {
      ...careRecord,
      residentId: +residentId,
      timestamp: formatUtcFetchTimestamp(
        date
          .local()
          .set('hour', hour!)
          .set('minute', minute!)
          .set('second', 0)
      ),
      staffName,
      workingTime,
      toiletType: !isEmptyToiletType && toiletType?.key ? String(toiletType?.key) : null,
      urinatedToilet: isUrinatedToilet,
      toiletVolume: toiletVolume ? +toiletVolume : null,
      padType: !isEmptyPadType && padType?.key ? String(padType?.key) : null,
      padUrineVolumeType,
      padUrineVolume: padUrineVolume ? +padUrineVolume : null,
      urineLeakage: isUrineLeakage,
      isUrgency,
      isLeakOutUrine,
      defecationVolumeType,
      hardnessType,
      isLeakOutFeces,
      diaper: {
        nursingHomeDiaperId: !isEmptyPantId && pantId?.key ? +pantId.key : null,
        nursingHomePadIdFirst: !isEmptyPad1Id && pad1Id?.key ? +pad1Id?.key : null,
        nursingHomePadIdSecond: !isEmptyPad2Id && pad2Id?.key ? +pad2Id.key : null,
      },
      laxativeId: !isEmptyLaxativeId && laxativeId?.key ? +laxativeId.key : null,
      laxativeAmount: laxativeAmount ? +laxativeAmount : null,
      drinkingId: !isEmptyDrinkId && drinkId?.key ? +drinkId.key : null,
      drinkingOther: otherDrink ? otherDrink : null,
      drinkingVolume: drinkVolumeType ? drinkVolumeType : drinkVolume ? +drinkVolume : null,
      memo,
    };

    const nullCheckKeys: Array<keyof ICreateCareRecordParams> = [
      'toiletType',
      'padType',
      'isUrgency',
      'isLeakOutUrine',
      'defecationVolumeType',
      'isLeakOutFeces',
      'laxativeId',
      'drinkingId',
      'drinkingVolume',
    ];

    const diaperNullCheckKeys: Array<keyof ICareRecordDiaper> = [
      'nursingHomeDiaperId',
      'nursingHomePadIdFirst',
      'nursingHomePadIdSecond',
    ];

    // バリデーションチェック
    if (dayjs().diff(dayjs(sendData.timestamp)) < 0) {
      setDateError(true);
      hasValidationErrors = true;
    }

    if (!!toiletType && !isEmptyToiletType && isUrinatedToilet === null) {
      setUrinatedToiletErrorText(t('enterYesOrNoForUrination', '排尿有無は必須入力です。'));
      hasValidationErrors = true;
    }

    if (!!padType && !isEmptyPadType && padUrineVolumeType === null) {
      setPadUrineVolumeTypeErrorText(t('enterUrinationVolume', '尿量は必須入力です。'));
      hasValidationErrors = true;
    }

    if (drinkId?.key === 0 && !sendData.drinkingOther) {
      setOtherDrinkError(true);
      setOtherDrinkErrorText(t('enterDrinkType', '飲料名は必須入力です。'));
      hasValidationErrors = true;
    }

    if (((!!drinkId?.key && !isEmptyDrinkId) || drinkId?.key === 0) && drinkVolumeType === '') {
      setDrinkVolumeTypeErrorText(t('enterWaterConsumptionVolume', '飲水量は必須入力です。'));
      hasValidationErrors = true;
    }

    if (laxativeId?.key && !isEmptyLaxativeId && !sendData.laxativeAmount) {
      setLaxativeAmountErrorText(t('enterTheAmountOfLaxative', '下剤量を入力してください。'));
      hasValidationErrors = true;
    }

    if (defecationVolumeType && defecationVolumeType !== 'none' && !hardnessType) {
      setHardnessTypeFormErrorText(t('enterDefecationHardnessType', '排便形態は必須入力です。'));
      hasValidationErrors = true;
    }

    if (hasValidationErrors) {
      showSnackbar(t('invalidFields', '入力内容に不備があります'), 'error');
      return;
    }

    // トイレかおむつのどちらかの排尿の記録がされた場合は片方が未選択でも無しとして補完する
    // また排尿の記録がある場合にはtoiletTypeとpadTypeも補完する
    sendData.urinatedToilet =
      sendData.urinatedToilet !== null ? sendData.urinatedToilet : sendData.padUrineVolumeType === null ? null : false;
    sendData.padUrineVolumeType =
      sendData.padUrineVolumeType !== null
        ? sendData.padUrineVolumeType
        : sendData.urinatedToilet === null
        ? null
        : 'none';
    sendData.toiletType = sendData.toiletType
      ? sendData.toiletType
      : sendData.urinatedToilet === null && sendData.padUrineVolumeType === null
      ? null
      : 'toilet';
    sendData.padType = sendData.padType
      ? sendData.padType
      : sendData.urinatedToilet === null && sendData.padUrineVolumeType === null
      ? null
      : 'pad';

    // 送信データが日付、作業記録、空のパンツ情報しかなければデータを送信しない
    const isSendDataEmpty = nullCheckKeys.every((keys) => sendData[keys] === null);
    const isDiaperDataEmpty = diaperNullCheckKeys.every((key) => sendData.diaper![key] === null);
    if (isSendDataEmpty && isDiaperDataEmpty && !sendData?.memo) {
      showSnackbar(t('thereIsNoNewRecordData', '登録する記録がありません'), 'error');
      return;
    }

    setOnSend(true);
    if (careRecordId && careRecord?.residentId === +residentId) {
      const data = { ...sendData, careRecordId: careRecord.diaper.careRecordId };
      await updateCareRecord(data).then((res) => {
        setOnSend(false);
        showSnackbar(t('detailedRecordPlus', '詳細記録＋') + t('isEdited', 'の編集が完了しました'), 'success');
        history.push(`/nursinghome/${nursingHomeId}/residents/${residentId}/care-record`);
      });
    } else {
      await createCareRecord(sendData).then(async (res) => {
        // ガスの通知がある場合はいかなる記録でも便関連のカバーを除去する（尿や飲水だけの記録でも除去）
        let coverTypes: string[] = ['gasPooped', 'gasPrediction'];

        // トイレ、おむつ・パッド、尿汚染の記録が有る場合は排泄されたとみなしグラフを落として、通知カバーも除去する
        if (
          sendData.urinatedToilet ||
          (sendData.padUrineVolumeType && sendData.padUrineVolumeType !== 'none') ||
          sendData.isLeakOutUrine
        ) {
          coverTypes = [...coverTypes, 'bigBladder', 'urination'];
          await updateUrineVolumeReset(sendData.residentId).catch((err) => {
            console.log(err);
          });
        }
        setTimeout(() => {
          handleResetNotification(sendData.residentId, coverTypes);
        }, 500);
        setOnSend(false);
        showSnackbar(t('detailedRecordPlus', '詳細記録＋') + t('isAdded', 'の登録が完了しました'), 'success');
        history.push(`/nursinghome/${nursingHomeId}/residents/${residentId}/care-record`);
      });
    }
  };

  // 通知アラートの消去とバッヂカウントの更新処理
  const handleResetNotification = React.useCallback(
    async (residentId: number, coverTypes?: string[]) => {
      let params = { residentId } as {
        residentId: number;
        uncaredBigBladder?: boolean;
        uncaredUrination?: boolean;
        uncaredGasPooped?: boolean;
        uncaredGasPrediction?: boolean;
        uncaredGotUp?: boolean;
        uncaredSensorError?: boolean;
        uncaredUrineReset?: boolean;
        uncaredZeroReset?: boolean;
        uncaredUpsideDown?: boolean;
      };

      const coverTypeMap: Record<string, keyof typeof params> = {
        bigBladder: 'uncaredBigBladder',
        urination: 'uncaredUrination',
        gasPooped: 'uncaredGasPooped',
        gasPrediction: 'uncaredGasPrediction',
        gotUp: 'uncaredGotUp',
        sensorError: 'uncaredSensorError',
        urineReset: 'uncaredUrineReset',
        zeroReset: 'uncaredZeroReset',
        upsideDown: 'uncaredUpsideDown',
      };

      const selectedCoverTypes = coverTypes ?? Object.keys(coverTypeMap);
      params = selectedCoverTypes.reduce((acc, type) => {
        const paramKey = coverTypeMap[type] as keyof typeof params;
        return paramKey ? { ...acc, [paramKey]: false } : acc;
      }, params);

      await api.put('/residents/:residentId/notifications/covers', params);
      if (badge.resident > 0) {
        dispatch(navSlice.actions.setResidentBadge(-1));
      }
    },
    [badge, nursingHomeId]
  );

  // 入力できないときは値を初期化する
  React.useEffect(() => {
    if (isDisabledIsUrinatedToiletForm) {
      setIsUrinatedToilet(null);
      setUrinatedToiletErrorText('');
    }
    if (isDisabledPadUrineVolumeTypeForm) {
      setPadUrineVolumeType(null);
      setPadUrineVolumeTypeErrorText('');
    }
    if (isDisabledDefecationVolumeForm) {
      setDefecationVolumeFormWarningText(
        t('YouCannotRecordABowelMovementAndRegisterALaxativeAtTheSameTime', '※下剤の登録と同時に排便記録はできません。')
      );
    } else {
      setDefecationVolumeFormWarningText('');
    }
    if (isDisabledLaxativeIdForm) {
      setLaxativeAmountFormWarningText(
        t('YouCannotRegisterALaxativeAndRecordABowelMovementAtTheSameTime', '※排便記録と同時に下剤の登録はできません。')
      );
    } else {
      setLaxativeAmountFormWarningText('');
    }
    if (isDisabledHardnessTypeForm) {
      setHardnessType(null);
    }
    if (isDisabledLeakOutFeces) {
      setIsLeakOutFeces(null);
    }
    if (isDisabledLaxativeAmountForm) {
      setLaxativeAmount('');
    }
    if (isDisabledDrinkVolumeTypeForm) {
      setDrinkVolumeType('');
      setDrinkVolumeTypeErrorText('');
    }
  }, [
    isDisabledIsUrinatedToiletForm,
    isDisabledPadUrineVolumeTypeForm,
    isDisabledDefecationVolumeForm,
    isDisabledHardnessTypeForm,
    isDisabledLeakOutFeces,
    isDisabledLaxativeIdForm,
    isDisabledLaxativeAmountForm,
    isDisabledDrinkVolumeTypeForm,
  ]);

  React.useEffect(() => {
    showLoading();
    if (resident) {
      hideLoading();
    }
  }, [resident]);

  // initialize-selector-form-value

  // toiletType
  React.useEffect(() => {
    if (!toiletTypeList.length || !careRecord) return;
    const result = toiletTypeList.find((v) => {
      return v.key === careRecord.toiletType;
    });
    setToiletType(result ?? null);
  }, [careRecord, toiletTypeList]);

  // padType
  React.useEffect(() => {
    if (!padTypeList.length || !careRecord) return;
    const result = padTypeList.find((v) => {
      return v.key === careRecord.padType;
    });
    setPadType(result ?? null);
  }, [careRecord, padTypeList]);

  // diaperId
  React.useEffect(() => {
    if (!pantsList.length || !careRecord) return;
    const result = pantsList.find((v) => {
      return v.key === careRecord.diaper.nursingHomeDiaperId;
    });
    setPantId(result ?? null);
  }, [careRecord, pantsList]);

  // PadIdFirst PadIdSecond
  React.useEffect(() => {
    if (!padsList.length || !careRecord) return;
    const pad1 = padsList.find((v) => {
      return v.key === careRecord.diaper.nursingHomePadIdFirst;
    });
    const pad2 = padsList.find((v) => {
      return v.key === careRecord.diaper.nursingHomePadIdSecond;
    });
    setPad1Id(pad1 ?? null);
    setPad2Id(pad2 ?? null);
  }, [careRecord, padsList]);

  // LaxativeId
  React.useEffect(() => {
    if (!laxativeList.length || !careRecord) return;
    const result = laxativeList.find((v) => {
      return v.key === careRecord.laxativeId;
    });
    setLaxativeId(result ?? null);
  }, [careRecord, laxativeList]);

  // drinkingId
  React.useEffect(() => {
    if (!drinkList.length || !careRecord) return;
    if (careRecord.drinkingOther) setDrinkId(drinkList[drinkList.length - 1]);
    else {
      const result = drinkList.find((v) => {
        return v.key === careRecord.drinkingId;
      });
      setDrinkId(result ?? null);
    }
  }, [careRecord, drinkList]);

  // Editの場合のsetter初期値設定
  React.useEffect(() => {
    showLoading();
    if (!careRecord || careRecord.residentId != +residentId) return;
    setDate(dayjs(careRecord.timestamp));
    // setHour(dayjs(careRecord.timestamp).hour()); //initialValueで定義
    // setMinute(dayjs(careRecord.timestamp).minute()); //initialValueで定義
    setStaffName(careRecord.staffName);
    setWorkingTime(careRecord.workingTime);
    setRuv(careRecord.ruv);
    setIsUrinatedToilet(careRecord.urinatedToilet);
    setToiletVolume(careRecord.toiletVolume);
    setPadUrineVolumeType(careRecord.padUrineVolumeType);
    setPadUrineVolume(careRecord.padUrineVolume);
    setIsUrineLeakage(careRecord.urineLeakage);
    setIsUrgency(careRecord.isUrgency);
    setIsLeakOutUrine(careRecord.isLeakOutUrine);
    setDefecationVolumeType(careRecord.defecationVolumeType);
    setHardnessType(careRecord.hardnessType);
    setIsLeakOutFeces(careRecord.isLeakOutFeces);
    setLaxativeAmount(careRecord.laxativeAmount);
    setOtherDrink(careRecord.drinkingOther);
    setDrinkVolumeType(careRecord.drinkingVolume ? 0 : ''); // TODO: 150と200は除外
    setDrinkVolume(careRecord.drinkingVolume);
    setMemo(careRecord.memo);
    hideLoading();
  }, [careRecord]);

  return (
    <PanelContainer title={t('detailedRecordPlus', '詳細記録＋')}>
      <>
        {resident && <div css={Title}>{resident?.lastName + ' ' + resident?.name}</div>}
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('datetime', '日時')}</div>
          <div css={[ContentRight, AlignItems, Wrap]}>
            <div css={W200}>
              <DateSelector
                newValue={date}
                minDate={initialDate.subtract(3, 'years')}
                maxDate={initialDate}
                variant={'outlined'}
                onChange={handleChangeDate}
              />
            </div>
            <div css={W200}>
              <Select
                id='hour-selector'
                listData={hourList}
                value={initialHour}
                label={t('hour', '時')}
                fullWidth={true}
                onChange={handleChangeHour}
              />
            </div>
            <div css={W200}>
              <Select
                id='minute-selector'
                listData={minuteList}
                value={initialMinute}
                label={t('Min', '分')}
                fullWidth={true}
                onChange={handleChangeMinute}
              />
            </div>
            {isDateError && (
              <div css={ErrorText}>
                {t('"Future date and time cannot be specified."', '未来の日時は指定できません。')}
              </div>
            )}
          </div>
        </div>
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('workRecord', '作業記録')}</div>
          <div css={[ContentRight, AlignItems, Wrap]}>
            <div css={W200}>
              <TextField
                sx={{ background: '#fff' }}
                id={'staffname-field'}
                label={t('staffName', '担当職員')}
                value={staffName ?? ''}
                onChange={handleChangeStaffName}
                fullWidth={true}
              />
            </div>
            <div css={[W200, AlignItems]}>
              <TextField
                sx={{ background: '#fff' }}
                id={'workingtime-field'}
                label={t('workingTime', 'ケア時間')}
                value={workingTime ?? ''}
                onChange={handleChangeWorkingTime}
                fullWidth={true}
              />
              <span css={Unit}>{t('minutes', '分')}</span>
            </div>
          </div>
        </div>
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('urine', '排尿')}</div>
          <div css={Wmax}>
            {!!ruv && (
              <div css={[ContentRight, AlignStart, Wrap]}>
                <span css={FormText}>残尿：{ruv}ml</span>
              </div>
            )}
            <div css={[ContentRight, AlignStart, Wrap]}>
              <div css={[W200, MarginTop, MarginBottom('12px')]}>
                <Select
                  id='toilet-type-selector'
                  listData={toiletTypeList}
                  value={toiletType}
                  label={t('urinationLocation', '排尿場所')}
                  fullWidth={true}
                  isEmptyValue={isEmptyToiletType}
                  onChange={handleChangeToiletType}
                />
              </div>
              <div css={[W200, ContentMargin]}>
                <div>{t('isUrinated', '排尿有無')}</div>
                <CareRecordButtonGroup
                  data={booleanButtonList}
                  newValue={isUrinatedToilet}
                  isDisabled={isDisabledIsUrinatedToiletForm}
                  onChange={handleChangeIsUrinatedToilet}
                ></CareRecordButtonGroup>
                {urinatedToiletErrorText && <div css={ErrorText}>{urinatedToiletErrorText}</div>}
              </div>
              <div css={[W200, AlignItems, MarginTop, MarginBottom('12px')]}>
                <TextField
                  id={'toilet-volume-field'}
                  label={t('urineAmount', '尿量')}
                  type={'number'}
                  value={toiletVolume ?? ''}
                  disabled={isDisableToiletVolumeForm}
                  onChange={handleChangeToiletVolume}
                  fullWidth={true}
                />
                <span css={Unit}>ml</span>
              </div>
            </div>
            <div css={[ContentRight, AlignItems, Wrap]}>
              <div css={[W200, MarginTop, MarginBottom('12px')]}>
                <Select
                  id='pad-type-selector'
                  listData={padTypeList}
                  value={padType}
                  label={t('padType', 'おむつ種別')}
                  fullWidth={true}
                  isEmptyValue={isEmptyPadType}
                  onChange={handleChangePadType}
                />
              </div>
              <div css={[W350, ContentMargin]}>
                <div>{t('urineAmount', '尿量')}</div>
                <CareRecordButtonGroup
                  data={padUrineVolumeTypeList}
                  isDisabled={isDisabledPadUrineVolumeTypeForm}
                  newValue={padUrineVolumeType}
                  onChange={handleChangePadUrineVolumeType}
                ></CareRecordButtonGroup>
                {padUrineVolumeTypeErrorText && <div css={ErrorText}>{padUrineVolumeTypeErrorText}</div>}
              </div>
              <div css={[W200, AlignItems, MarginTop, MarginBottom('12px')]}>
                <TextField
                  id={'pad-volume-field'}
                  label={t('urineAmount', '尿量')}
                  type={'number'}
                  value={padUrineVolume ?? ''}
                  disabled={isDisabledPadUrineVolumeForm}
                  onChange={handleChangePadUrineVolume}
                  fullWidth={true}
                />
                <span css={Unit}>ml</span>
              </div>
            </div>
            {showMedicalContent && (
              <div css={[ContentRight, AlignItems, Wrap]}>
                <div css={[W300, ContentMargin]}>
                  <div>{t('urineLeakage', '尿漏れ')}</div>
                  <CareRecordButtonGroup
                    data={booleanButtonList}
                    newValue={isUrineLeakage}
                    isDisabled={isDisabledUrgency}
                    onChange={handleChangeIsUrineLeakage}
                  ></CareRecordButtonGroup>
                </div>
              </div>
            )}
            {showMedicalContent && (
              <div css={[ContentRight, AlignItems, Wrap]}>
                <div css={[W300, ContentMargin]}>
                  <div>{t('isUrgency', '切迫感')}</div>
                  <CareRecordButtonGroup
                    data={booleanButtonList}
                    newValue={isUrgency}
                    isDisabled={isDisabledUrgency}
                    onChange={handleChangeIsUrgency}
                  ></CareRecordButtonGroup>
                </div>
              </div>
            )}
            <div css={[ContentRight, AlignItems, Wrap]}>
              <div css={[W300, ContentMargin]}>
                <div>{t('contaminationOfClothesAndSheets', '衣服・シーツ汚染')}</div>
                <CareRecordButtonGroup
                  data={booleanButtonList}
                  newValue={isLeakOutUrine}
                  isDisabled={isDisabledUrgency}
                  onChange={handleChangeIsUrinePollution}
                ></CareRecordButtonGroup>
              </div>
            </div>
          </div>
        </div>
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('stool', '排便')}</div>
          <div css={Wmax}>
            <div css={[ContentRight, AlignStart, Wrap, ContentGap, DirectionColumn]}>
              {defecationVolumeFormWarningText && <div css={ErrorText}>{defecationVolumeFormWarningText}</div>}
              <div css={W400}>
                <div>{t('defecation', '排便量')}</div>
                <CareRecordButtonGroup
                  data={defecationVolumeTypeList}
                  newValue={defecationVolumeType}
                  isDisabled={isDisabledDefecationVolumeForm}
                  onChange={handleChangeDefecationVolumeType}
                ></CareRecordButtonGroup>
              </div>
            </div>
            <div css={[ContentRight, AlignItems, Wrap]}>
              <div css={[Wmax, ContentMargin]}>
                <div>{t('defecationHardnessType', '排便形態')}</div>
                <CareRecordButtonGroup
                  data={hardnessTypeList}
                  newValue={hardnessType}
                  isDisabled={isDisabledHardnessTypeForm}
                  onChange={handleChangeHardnessType}
                ></CareRecordButtonGroup>
                {hardnessTypeFormErrorText && <div css={ErrorText}>{hardnessTypeFormErrorText}</div>}
              </div>
            </div>
            <div css={[ContentRight, AlignItems, Wrap]}>
              <div css={[W300, ContentMargin]}>
                <div>{t('contaminationOfClothesAndSheets', '衣服・シーツ汚染')}</div>
                <CareRecordButtonGroup
                  data={booleanButtonList}
                  newValue={isLeakOutFeces}
                  isDisabled={isDisabledLeakOutFeces}
                  onChange={handleChangeIsStoolPollution}
                ></CareRecordButtonGroup>
              </div>
            </div>
          </div>
        </div>
        {i18n.language.startsWith('ja') && (
          <div css={ContentWrapper}>
            <div css={ContentLeft}>{t('use', '交換')}</div>
            <div css={Wmax}>
              <div css={[ContentRight, AlignItems]}>
                <div css={[W100, AlignItems]}>
                  <img css={Image} src='/images/recordPlus/pant.svg' alt={t('diaperPants', 'テープ/パンツ')} />
                  <span css={TableLabel}>{t('diaperPants', 'テープ/パンツ')}</span>
                </div>
                <div css={Wmax}>
                  <Select
                    id='pant-selector'
                    listData={pantsList}
                    value={pantId}
                    label={t('diaperPants', 'テープ/パンツ')}
                    fullWidth={true}
                    isEmptyValue={isEmptyPantId}
                    onChange={handleChangePantId}
                  />
                </div>
              </div>
              <div css={[ContentRight, ContentWrapper, AlignItems]}>
                <div css={[W100, AlignItems]}>
                  <img css={Image} src='/images/recordPlus/pad.svg' alt={t('pad', 'パッド')} />
                  <span css={TableLabel}>{t('pad', 'パッド')}</span>
                </div>
                <div css={Wmax}>
                  <div css={[Wmax, MarginBottom('6px')]}>
                    <Select
                      id='pad-1-selector'
                      listData={padsList}
                      value={pad1Id}
                      label={t('firstPad', 'パッド1枚目')}
                      fullWidth={true}
                      isEmptyValue={isEmptyPad1Id}
                      onChange={handleChangePad1Id}
                    />
                  </div>
                  <div css={Wmax}>
                    <Select
                      id='pad-2-selector'
                      listData={padsList}
                      value={pad2Id}
                      label={t('secondPad', 'パッド2枚目')}
                      fullWidth={true}
                      disabled={isDisabledPad2IdForm}
                      isEmptyValue={isEmptyPad2Id}
                      onChange={handleChangePad2Id}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('laxative', '下剤')}</div>
          <div css={[ContentRight, AlignStart, Wrap, DirectionColumn]}>
            {laxativeAmountFormWarningText && <div css={ErrorText}>{laxativeAmountFormWarningText}</div>}
            <div css={[AlignStart, Wrap, ContentGap]}>
              <div css={W200}>
                <Select
                  id='laxative-selector'
                  listData={laxativeList}
                  value={laxativeId}
                  disabled={isDisabledLaxativeIdForm}
                  label={t('laxativeName', '下剤名')}
                  fullWidth={true}
                  isEmptyValue={isEmptyLaxativeId}
                  onChange={handleChangeLaxativeId}
                />
              </div>
              <div css={W200}>
                <TextField
                  id={'laxative-amount-field'}
                  label={t('laxativeAmount', '下剤量')}
                  type={'number'}
                  value={laxativeAmount ?? ''}
                  disabled={isDisabledLaxativeAmountForm}
                  error={!!laxativeAmountErrorText}
                  helperText={laxativeAmountErrorText}
                  onChange={handleChangeLaxativeAmount}
                  fullWidth={true}
                />
              </div>
            </div>
          </div>
        </div>
        <div css={ContentWrapper}>
          <div css={ContentLeft}>{t('waterIntake', '飲水')}</div>
          <div css={Wmax}>
            <div css={[ContentRight, AlignStart, Wrap]}>
              <div css={W200}>
                <Select
                  id='drink-type-selector'
                  listData={drinkList}
                  value={drinkId}
                  label={t('drinkName', '飲水名')}
                  fullWidth={true}
                  isEmptyValue={isEmptyDrinkId}
                  onChange={handleChangeDrinkId}
                />
              </div>
              <div css={W200}>
                <TextField
                  id={'other-drink-field'}
                  label={t('other', 'その他')}
                  value={otherDrink ?? ''}
                  disabled={isDisabledOtherDrinkForm}
                  error={isOtherDrinkError}
                  helperText={otherDrinkErrorText}
                  onChange={handleChangeOtherDrink}
                  fullWidth={true}
                />
              </div>
            </div>
            <div css={[ContentRight, AlignItems, Wrap]}>
              <div css={[W300, ContentMargin]}>
                <div>{t('drinkingVolume', '飲水量')}</div>
                <CareRecordButtonGroup
                  data={drinkVolumeList}
                  isDisabled={isDisabledDrinkVolumeTypeForm}
                  onChange={handleChangeDrinkVolumeType}
                  newValue={drinkVolumeType}
                ></CareRecordButtonGroup>
                {drinkVolumeTypeErrorText && <div css={ErrorText}>{drinkVolumeTypeErrorText}</div>}
              </div>
              <div css={[W200, AlignItems, MarginTop, MarginBottom('12px')]}>
                <TextField
                  id={'drink-volume-field'}
                  label={t('drinkingVolume', '飲水量')}
                  type={'number'}
                  value={drinkVolume ?? ''}
                  disabled={isDisabledDrinkVolumeForm}
                  onChange={handleChangeDrinkVolume}
                  fullWidth={true}
                />
                <span css={Unit}>ml</span>
              </div>
            </div>
          </div>
        </div>
        <div css={ContentWrapper}>
          <div css={[ContentLeft, BottomLine]}>{t('memoDetailRecord', '経過記録・メモ')}</div>
          <div css={[ContentRight, AlignItems, Wrap, BottomLine]}>
            <div css={[Wmax, ContentMargin]}>
              <TextField
                sx={{ background: '#fff' }}
                id={'memo-field'}
                label={t('memoDetailRecord', '経過記録・メモ')}
                value={memo ?? ''}
                onChange={handleChangeMemo}
                fullWidth={true}
                multiline
                rows={4}
              />
            </div>
          </div>
        </div>
        <div css={[ButtonWrapper]}>
          <Button variant={'contained'} onClick={handleClickSubmit} disabled={onSend}>
            {t('record', '記録する')}
          </Button>
        </div>
      </>
    </PanelContainer>
  );
});
